/**
 * Session Storage Cache for ODB Fleets
 * 
 * Caches ODB fleet data from UAT API calls to improve performance and reduce redundant requests.
 * Cache expires after 5 minutes.
 * 
 * Cache key format: `odbfleets-{environment}-{productId}-{serviceDate}-{timezone}`
 */

import type { Environment } from './EnvironmentSelector';
import type { UATShiftSolutionsResponse } from '../../../services/fixedRouteUatClient';

const CACHE_PREFIX = 'odbfleets';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

interface CachedODBFleetsData {
  data: UATShiftSolutionsResponse;
  timestamp: number;
  environment: Environment;
  productId: string;
  serviceDate: string;
  timezone: string;
}

/**
 * Get cached ODB fleets data if it exists and hasn't expired
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product ID
 * @param serviceDate - Service date (YYYY-MM-DD)
 * @param timezone - Timezone string
 * @returns Cached data or null if not found or expired
 */
export function getCachedODBFleetsList(
  environment: Environment,
  productId: string,
  serviceDate: string,
  timezone: string
): UATShiftSolutionsResponse | null {
  try {
    const cacheKey = `${CACHE_PREFIX}-${environment}-${productId}-${serviceDate}-${timezone}`;
    const cachedStr = sessionStorage.getItem(cacheKey);
    
    if (!cachedStr) {
      return null;
    }
    
    const cached: CachedODBFleetsData = JSON.parse(cachedStr);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - cached.timestamp > CACHE_EXPIRATION_MS) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    
    // Verify cache matches current parameters
    if (cached.environment !== environment ||
        cached.productId !== productId ||
        cached.serviceDate !== serviceDate ||
        cached.timezone !== timezone) {
      // Cache key mismatch, remove it
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    
    console.log(`[getCachedODBFleetsList] Cache hit for ${cacheKey}`);
    return cached.data;
  } catch (error) {
    console.warn('[getCachedODBFleetsList] Error reading cache:', error);
    return null;
  }
}

/**
 * Cache ODB fleets data in session storage
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product ID
 * @param serviceDate - Service date (YYYY-MM-DD)
 * @param timezone - Timezone string
 * @param data - ODB fleets data to cache
 */
export function setCachedODBFleetsList(
  environment: Environment,
  productId: string,
  serviceDate: string,
  timezone: string,
  data: UATShiftSolutionsResponse
): void {
  try {
    const cacheKey = `${CACHE_PREFIX}-${environment}-${productId}-${serviceDate}-${timezone}`;
    const cached: CachedODBFleetsData = {
      data,
      timestamp: Date.now(),
      environment,
      productId,
      serviceDate,
      timezone,
    };
    
    sessionStorage.setItem(cacheKey, JSON.stringify(cached));
    console.log(`[setCachedODBFleetsList] Cached ODB fleets for ${cacheKey}`);
  } catch (error) {
    console.warn('[setCachedODBFleetsList] Error writing cache:', error);
    // Cache write failures are not critical, just log and continue
  }
}

